import type { MutableRefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { TableRef } from 'components/common/Table/Table';
import type { CreateHeliosDataTableRecordReq, GetSingleHeliosRecord } from 'types/dataHelios';
import { processDirtyFieldsToRows } from '../../helios/row';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import type { TableRow } from 'components/common/Table/tableFunctions';
import { createEmptyRow } from 'components/common/Table/tableFunctions';
import type { DataSchema, Schema, UserAgendaTableDataSchemaType } from 'types';
import { useHeliosSaveRecord } from '../useHeliosSaveRecord';
import type { SaveAllRowsResult, SaveRowData } from 'types/helios/record';
import { FormValidationException } from '../../error/FormValidationException';
import { TABLE_CELL_TYPE } from 'types/enums';
import type { HeliosCellDataExtended, HeliosColumnsCellData } from 'types/helios/agendaListing';
import { countHeliosBrowseItems } from '../../helios/fieldValidation';
import { useLazyGetHeliosDataRecordsPrefilledQuery } from 'store/services/helios/dataHelios';
import { replaceKey } from 'utils/formItemsDots';
import { isObjectEmpty } from 'utils/typeChecks';

export type OriginalDataType = Schema<string, DataSchema>[] | HeliosColumnsCellData[];

type SetFieldError = (name: string, error: { type?: string; message?: string }, options?: { shouldFocus: boolean }) => void;

const createOriginalDataMapGetter = (originalData: OriginalDataType) => {
  let originalDataMap: Map<string, HeliosColumnsCellData> | undefined;
  return () => {
    if (!originalDataMap) {
      originalDataMap = new Map<string, HeliosColumnsCellData>();
      for (const originalRow of originalData) {
        originalDataMap.set(originalRow.id, originalRow as HeliosColumnsCellData);
      }
    }
    return originalDataMap;
  };
};

export const validateRequiredFieldsAndGetErrors = (
  dirtyFields: Record<string, Record<string, boolean | undefined> | undefined>,
  formData: Record<string, Record<string, unknown>>,
  dataSchema: DataSchema,
  originalData: OriginalDataType,
  setFieldError: SetFieldError | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string | undefined => {
  const missingRequiredColumnKeys: Record<string, number> = {};

  const setRequiredFieldError = (columnKey: string, rowId: string): void => {
    missingRequiredColumnKeys[columnKey] = (missingRequiredColumnKeys[columnKey] || 0) + 1;
    const keyCount = Object.keys(missingRequiredColumnKeys).length;
    if (keyCount === 1) {
      setFieldError?.(`${rowId}.${columnKey}`, { type: 'required', message: t('common.required') }, { shouldFocus: true });
    } else {
      setFieldError?.(`${rowId}.${columnKey}`, { type: 'required', message: t('common.required') });
    }
  };

  const getOriginalDataMap = createOriginalDataMapGetter(originalData);

  for (const rowId in dirtyFields) {
    const dirtyFieldsForRow = dirtyFields[rowId];
    if (!dirtyFieldsForRow) continue;

    const rowData = formData[rowId] || {};
    for (const columnKey in rowData) {
      const fieldDataSchema = dataSchema[columnKey as keyof typeof dataSchema];
      const isFieldRequired = fieldDataSchema?.isRequired;
      const isFieldEditable = fieldDataSchema?.isEditable;
      const fieldValue = formData[rowId][columnKey];

      if (isFieldRequired && isFieldEditable) {
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
          setRequiredFieldError(columnKey, rowId);
        } else {
          const getIsSingleBrowseOrNullIfHandledByFalsyChecksAbove = (): boolean | null => {
            switch (fieldDataSchema?.type) {
              case TABLE_CELL_TYPE.HELIOS_BROWSE:
                return !fieldDataSchema?.settings?.browse?.dynamicRelationId && !fieldDataSchema?.settings?.browse?.allowMultipleSelection;
              case TABLE_CELL_TYPE.HELIOS_FILE:
                return !fieldDataSchema?.settings?.externalFile?.dynamicRelationId;
              default:
                return null;
            }
          };
          const isSingleBrowse = getIsSingleBrowseOrNullIfHandledByFalsyChecksAbove();
          if (isSingleBrowse !== null) {
            const checkHeliosSingleBrowse = () => {
              const isObject = typeof fieldValue === 'object';
              if (isObject) {
                const browseRecord = fieldValue as Record<string, unknown>;
                const hasIdKey = 'id' in browseRecord;
                const hasNameKey = 'name' in browseRecord;
                const hasReferenceKey = 'reference' in browseRecord;

                if (
                  !hasIdKey ||
                  !hasNameKey ||
                  (browseRecord['name'] === null &&
                    fieldDataSchema?.type === TABLE_CELL_TYPE.HELIOS_BROWSE &&
                    hasReferenceKey &&
                    browseRecord['reference'] === null)
                ) {
                  setRequiredFieldError(columnKey, rowId);
                }
              }
            };

            const checkHeliosDynamicRelationBrowse = () => {
              const originalDataMap = getOriginalDataMap();
              const originalRow = originalDataMap.get(rowId);

              if (originalRow) {
                const cellData = (originalRow as HeliosColumnsCellData)[columnKey as keyof HeliosColumnsCellData] as HeliosCellDataExtended;
                const totalFilesCount = countHeliosBrowseItems(cellData, fieldValue);
                if (totalFilesCount === 0) {
                  setRequiredFieldError(columnKey, rowId);
                }
              }
            };

            const checkHeliosDynamicRelationFile = () => {
              const fieldValueObj = fieldValue as { add: number[]; remove: number[] };
              const originalDataMap = getOriginalDataMap();
              const originalRow = originalDataMap.get(rowId);

              if (originalRow) {
                const cellData = (originalRow as HeliosColumnsCellData)[columnKey as keyof HeliosColumnsCellData] as HeliosCellDataExtended;
                const existingFiles = cellData?.dynamicRelationValuesCount ?? 0;
                const addedItems = Array.isArray(fieldValueObj.add) ? fieldValueObj.add : [];
                const removedItems = Array.isArray(fieldValueObj.remove) ? fieldValueObj.remove : [];

                const totalFilesCount = existingFiles + addedItems.length - removedItems.length;
                if (totalFilesCount === 0) {
                  setRequiredFieldError(columnKey, rowId);
                }
              }
            };

            if (isSingleBrowse) {
              checkHeliosSingleBrowse();
            } else {
              if (Array.isArray(fieldValue)) {
                checkHeliosDynamicRelationBrowse();
              } else if (typeof fieldValue === 'object' && fieldValue !== null) {
                checkHeliosDynamicRelationFile();
              }
            }
          }
        }
      }
    }
  }

  const missingRequiredColumnKeysArray = Object.keys(missingRequiredColumnKeys);

  const errorMessage = missingRequiredColumnKeysArray
    .map((columnKey) => {
      const columnName = dataSchema[columnKey as keyof typeof dataSchema]?.customKey || columnKey;
      const missingCount = missingRequiredColumnKeys[columnKey];
      return missingCount > 1
        ? t('common.requiredWithFieldNameAndCount', { fieldName: columnName, count: missingCount })
        : t('common.requiredWithFieldName', { fieldName: columnName });
    })
    .join('\n');

  return errorMessage || undefined;
};

/**
 * Copies the values returned by the Prefilled endpoint into a freshly created empty row.
 * Columns missing from the table schema are skipped. Returns a new row, the input is not mutated.
 */
export const applyPrefilledValuesToRow = <T extends Record<string, unknown>>(row: T, prefilled?: GetSingleHeliosRecord): T => {
  const sectionItems = prefilled?.sections?.flatMap((section) => section.items ?? []) ?? [];
  const prefilledRow: Record<string, unknown> = { ...row };

  for (const sectionItem of sectionItems) {
    const column = sectionItem.dataRecordColumn;
    if (!column) continue;

    const columnKey = replaceKey(column.columnKey);
    if (!(columnKey in prefilledRow)) continue;

    prefilledRow[columnKey] = {
      rawValue: isObjectEmpty(column.rawValue) ? '' : column.rawValue,
      unit: column.unit,
      dynamicRelationValuesCount: column.dynamicRelationValuesCount,
      columnType: column.columnType ?? null,
      itemType: column.itemType,
      userDefinedAttributeType: column.userDefinedAttributeType,
      recordNumber: 0,
      thousandsSeparator: column.thousandsSeparator,
      uiDefinition: sectionItem.uiDefinition ?? null,
    } as HeliosCellDataExtended;
  }

  return prefilledRow as T;
};

interface UseHeliosAgendaTableActionsProps {
  masterRecordId?: string;
  templateId?: string;
  parentTemplateId?: string;
  dataSchema: DataSchema;
  refetch?: (result: SaveAllRowsResult) => Promise<void> | void;
  initialAddedRows?: TableRow<string, UserAgendaTableDataSchemaType>[];
  originalData: OriginalDataType;
}

export type HeliosRowData = Pick<CreateHeliosDataTableRecordReq, 'templateId' | 'columns'>;

export const useHeliosAgendaTableActions = ({
  masterRecordId,
  templateId = '',
  parentTemplateId,
  dataSchema,
  refetch,
  initialAddedRows,
  originalData,
}: UseHeliosAgendaTableActionsProps) => {
  const { t } = useTranslation();
  const [savingData, setSavingData] = useState<boolean>(false);
  const [isAddingRow, setIsAddingRow] = useState<boolean>(false);
  const isAddingRowRef = useRef<boolean>(false);
  const [addedRows, setAddedRows] = useState<(TableRow<string, UserAgendaTableDataSchemaType> & { originalRowId?: string })[]>(
    initialAddedRows && initialAddedRows.length ? initialAddedRows : []
  );

  const { saveAllRows } = useHeliosSaveRecord();
  const [triggerGetPrefilled] = useLazyGetHeliosDataRecordsPrefilledQuery();

  const resetFormState = useCallback((tableRef: TableRef | null) => {
    setAddedRows([]);
    tableRef?.resetSavedForm();
  }, []);

  const handleOnSaveEdit = useCallback(
    async (tableRef: MutableRefObject<TableRef | null>): Promise<void> => {
      const currentTableRef = tableRef.current;

      const isValid = Boolean(await tableRef.current?.triggerForm());

      if (!isValid) {
        throw new FormValidationException();
      }

      const formData = tableRef.current?.getValues() as Record<string, Record<string, unknown>> | undefined;
      if (!formData) {
        return;
      }

      const values = tableRef.current?.getFormState().dirtyFields;
      if (!values || Object.keys(values).length === 0) {
        return;
      }

      const errorMessageRequiredFieldMissing = validateRequiredFieldsAndGetErrors(
        values,
        formData,
        dataSchema,
        originalData,
        tableRef.current?.setError,
        t
      );

      if (errorMessageRequiredFieldMissing) {
        toast.error(errorMessageRequiredFieldMissing, {
          style: { whiteSpace: 'pre-line' },
        });
        return;
      }

      setSavingData(true);

      try {
        const rows = processDirtyFieldsToRows(values, formData, dataSchema);
        const data = {
          templateId,
          masterRecordId,
          dataSchema,
        } satisfies SaveRowData;

        const saveResult = await saveAllRows(rows, formData, data, tableRef);
        const { failedUpdates } = saveResult;

        if (failedUpdates.length > 0) {
          for (const { rowId: failedRowId } of failedUpdates) {
            toast.error(t('helios.listing.error.editRow', { rowId: failedRowId }));
          }
          throw new Error('Failed to save rows');
        }

        toast.success(t('helios.listing.success.editRow'));
        resetFormState(currentTableRef);

        if (refetch) {
          await Promise.resolve(refetch(saveResult));
        }
      } catch (e) {
        toast.error(t('common.error'));

        throw e;
      } finally {
        setSavingData(false);
      }
    },
    [dataSchema, originalData, t, templateId, masterRecordId, saveAllRows, resetFormState, refetch]
  );

  const handleRowAdd = useCallback(async () => {
    const emptyRow = createEmptyRow(dataSchema);
    const canPrefill = Boolean(parentTemplateId && masterRecordId && templateId);

    if (!canPrefill) {
      setAddedRows((prevState) => [emptyRow, ...prevState]);
      return;
    }

    if (isAddingRowRef.current) return;
    isAddingRowRef.current = true;
    setIsAddingRow(true);

    let newRow = emptyRow;

    try {
      const prefilled = await triggerGetPrefilled({
        templateId,
        parentTemplateId,
        parentRecordNumber: masterRecordId,
      }).unwrap();

      newRow = applyPrefilledValuesToRow(emptyRow, prefilled);
    } catch (e) {
      // The row is still usable without prefilled values, so we only report the failure.
      // eslint-disable-next-line no-console
      console.warn('Prefilling a new row from the parent record failed:', e);
    } finally {
      isAddingRowRef.current = false;
      setIsAddingRow(false);
    }

    setAddedRows((prevState) => [newRow, ...prevState]);
  }, [dataSchema, parentTemplateId, masterRecordId, templateId, triggerGetPrefilled]);

  const insertRowCopy = useCallback(
    (row: { id: string; identifier: string }) => {
      const newUndefinedRow = createEmptyRow(dataSchema);
      const newRowCleaned = Object.fromEntries(Object.entries(newUndefinedRow).filter(([, value]) => value !== undefined)) as TableRow<
        string | number,
        DataSchema
      >;

      const originalRowId = row.id;
      const newFilledRow = { ...row, ...newRowCleaned } as TableRow<string | number, DataSchema>;
      (newFilledRow as unknown as { originalRowId: string }).originalRowId = originalRowId;
      // dirty
      setAddedRows((prevState) => {
        return [newFilledRow, ...prevState];
      });
    },
    [dataSchema]
  );

  const handleCancelEditRow = useCallback((rowId: string) => {
    setAddedRows((prevState) => prevState.filter((row) => row.identifier !== rowId));
  }, []);

  return { addedRows, handleRowAdd, insertRowCopy, handleCancelEditRow, handleOnSaveEdit, savingData, isAddingRow };
};
